import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import MenuIcon from "@material-ui/icons/Menu";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import AccountCircle from "@material-ui/icons/AccountCircle";
import LogoutIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/Settings";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Tooltip from "@material-ui/core/Tooltip";
import BetDateFilter from "./BetDateFilter";
import BetStatusFilter from "./BetStatusFilter";
import ClientSelector from "./ClientSelector";
import { useMsal } from "@azure/msal-react";
import { useRecoilState } from "recoil";
import { themeState } from "../../../Atoms/uiAtom";
import { selectedClientIdState } from "../../../Atoms/userContextAtom";
import { useUserContextQuery } from "../../../hooks/useUserContextQuery";
import { useClientsQuery } from "../../../hooks/useClientsQuery";
import { trimPath } from "../../utilities";
import { loginRequest } from "../../../authConfig";

const Header = (props) => {
  const { drawerOpen, handleDrawer, routes } = props;
  const classes = useStyles();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const name = accounts[0]?.name;
  const [theme, setTheme] = useRecoilState(themeState);
  const isDark = theme === "darkTheme";
  const [selectedClientId, setSelectedClientId] = useRecoilState(
    selectedClientIdState,
  );
  const userContextQuery = useUserContextQuery();
  const userContext = userContextQuery.data;
  const clientsQuery = useClientsQuery(userContext?.isAdmin);
  const [photoUrl, setPhotoUrl] = useState(null);
  const photoFetched = useRef(false);

  useEffect(() => {
    if (!accounts[0] || photoFetched.current) return;
    photoFetched.current = true;

    instance
      .acquireTokenSilent({ scopes: loginRequest.scopes, account: accounts[0] })
      .then((response) =>
        fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: "Bearer " + response.accessToken },
        }),
      )
      .then((res) => {
        if (!res.ok) return;
        return res.blob();
      })
      .then((blob) => {
        if (blob) setPhotoUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
  }, [instance, accounts]);

  const routeIndex = routes.findIndex(
    (route) => trimPath(route.path) === trimPath(location.pathname),
  );

  // User Menu
  const [anchorEl, setAnchorEl] = useState(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const logout = (instance) => {
    instance.logout();
  };

  const MENU_ITEM_HEIGHT = 48;
  const menuId = "user-menu";

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        style: {
          maxHeight: MENU_ITEM_HEIGHT * 6.5,
          minWidth: "180px",
        },
      }}
    >
      <Box py={1} px={2}>
        <Typography component="div" variant="caption">
          Signed in as
        </Typography>
        <Typography component="div" variant="subtitle2">
          {name}
        </Typography>
      </Box>
      <Divider />
      <MenuItem component={Link} to="/settings">
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="Settings">Settings</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => logout(instance)}>
        <ListItemIcon>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary="Logout">Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <div>
      <AppBar
        position="fixed"
        className={`${classes.appBar} ${
          drawerOpen ? classes.appBarShift : null
        }`}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            aria-label="Open Drawer"
            onClick={() => handleDrawer()}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" ml={2}>
            <Typography variant="h6" component="h2" className={classes.title}>
              {routeIndex === -1 ? "Page Not Found" : routes[routeIndex].title}
            </Typography>
          </Box>
          <div className={classes.grow} />
          {location.pathname.includes("bet-monitor") && (
            <>
              <BetStatusFilter />
              <BetDateFilter />
            </>
          )}
          <ClientSelector
            userContext={userContext}
            clients={clientsQuery.data}
            selectedClientId={selectedClientId}
            onClientChange={setSelectedClientId}
          />
          <Tooltip
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            <IconButton
              aria-label="toggle dark mode"
              onClick={() => setTheme(isDark ? "defaultTheme" : "darkTheme")}
              color="inherit"
            >
              {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-controls={menuId}
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            {photoUrl ? (
              <Avatar src={photoUrl} alt={name} className={classes.avatar} />
            ) : (
              <AccountCircle />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>
      {renderMenu}
    </div>
  );
};

export default Header;

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    "& .MuiToolbar-gutters": {
      paddingLeft: theme.spacing(1),
    },
  },
  appBarShift: {
    width: `calc(100% - ${theme.layout.drawerWidth}px)`,
    marginLeft: theme.layout.drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  grow: {
    flexGrow: 1,
  },
  clock: {
    flexShrink: 0,
    marginRight: theme.spacing(1),
  },
  title: {
    textTransform: "capitalize",
  },
  avatar: {
    width: 28,
    height: 28,
  },
}));
